import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  IconButton,
  Chip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  SignalCellularConnectedNoInternet0Bar as DisconnectedIcon,
  SignalCellularConnectedNoInternet4Bar as ConnectedIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import ScoreGradientBar from './ScoreGradientBar';
import { mockHistoryData, generateNewMockEvents } from '../libs/mockData';
import { USE_MOCK_DATA, streamEventToDevice } from './Devices';
import { fetchApiRequest, getApiHost } from './Common';
import { useSSE } from '../hooks/useSSE';

function History({ displayMode = 'circular' }) {
  const theme = useTheme();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const itemsPerPage = 20;
  const componentId = useRef(`DeviceHistory-${Date.now()}`);

  const { data: liveEvents, status: connectionStatus, error: sseError } = useSSE('/v1/events/stream', {
    fallbackToPolling: true,
    pollInterval: 5000, // 5 seconds
  });




  // Memory cleanup mechanism - reload entire history periodically to prevent memory buildup
  useEffect(() => {
    // Wait for initial data to load before starting cleanup
    if (history.length === 0) return;

    const memoryCleanupInterval = setInterval(() => {

      // Store the current history temporarily to preserve any new events
      const currentHistory = [...history];

      // Clear current history to free memory
      setHistory([]);
      setCurrentPage(1);
      setLoading(true);

      if (USE_MOCK_DATA) {
        // For mock data, reload with fresh mock data and merge with any new events
        setTimeout(() => {
          const refreshedData = mockHistoryData.map(event => ({
            ...event,
            timestamp: new Date().toISOString() // Update timestamps to current time
          }));

          // Find any events in currentHistory that are newer than the mock data
          const mockDataTimestamp = new Date(refreshedData[0]?.timestamp || new Date()).getTime();
          const newerEvents = currentHistory.filter(event => {
            const eventTimestamp = new Date(event.timestamp).getTime();
            return eventTimestamp > mockDataTimestamp;
          });

          // Combine refreshed data with newer events
          const combinedData = [...newerEvents, ...refreshedData];

          const sortedRefreshedData = combinedData.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });


          setHistory(sortedRefreshedData);
          setLastUpdate(new Date());
          setLoading(false);
        }, 500);
      } else {
        // For real API, fetch fresh data from scratch and merge with any new events
        getApiHost(apiHost => {
          fetchApiRequest(`${apiHost}/v1/devices/history?limit=100&cleanup=${Date.now()}`, (fetchPromise) => {
            fetchPromise
              .then(res => res.ok ? res.json() : Promise.reject('Network response was not ok'))
              .then(data => {
                // Normalize timestamps to local time by subtracting 2 hours
                const normalizedData = data.map(event => ({
                  ...event,
                  timestamp: new Date(new Date(event.timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
                }));

                const sortedData = normalizedData.sort((a, b) => {
                  const dateA = new Date(a.timestamp);
                  const dateB = new Date(b.timestamp);
                  return dateB.getTime() - dateA.getTime();
                });

                // Find any events in currentHistory that are newer than the API data
                const apiDataTimestamp = new Date(sortedData[0]?.timestamp || new Date()).getTime();
                const newerEvents = currentHistory.filter(event => {
                  const eventTimestamp = new Date(event.timestamp).getTime();
                  return eventTimestamp > apiDataTimestamp;
                });

                // Combine API data with newer events
                const combinedData = [...newerEvents, ...sortedData];

                const finalSortedData = combinedData.sort((a, b) => {
                  const dateA = new Date(a.timestamp);
                  const dateB = new Date(b.timestamp);
                  return dateB.getTime() - dateA.getTime();
                });


                setHistory(finalSortedData);
                setLastUpdate(new Date());
                setLoading(false);
              })
              .catch(err => {
                setError(err.toString());
                setLoading(false);
              });
          });
        });
      }
    }, 120000); // Cleanup every 2 minutes (120,000 ms)

    return () => clearInterval(memoryCleanupInterval);
  }, [history.length]);

  // Mock live updates for testing - generates new events every 15 seconds
  useEffect(() => {
    if (USE_MOCK_DATA) {
      const mockInterval = setInterval(() => {
        const newEvent = generateNewMockEvents();

        setHistory(prevHistory => {

          const newEvents = [newEvent].map(e => ({
            ...e,
            device_id: e.name || e.device_id,
            timestamp: e.timestamp,
            events: e.events || [],
            scores: e.scores || []
          }));

          const incomingEvents = newEvents.filter(newEvent => {
            const isDuplicate = prevHistory.some(existingEvent => {
              const duplicateCheck = existingEvent.timestamp === newEvent.timestamp &&
                                   existingEvent.device_id === newEvent.device_id;
              return duplicateCheck;
            });
            return !isDuplicate;
          });

          if (incomingEvents.length === 0) {
            return prevHistory;
          }

          const combined = [...incomingEvents, ...prevHistory];
          combined.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });

          // Reset to first page so user sees new events immediately
          setCurrentPage(1);
          setLastUpdate(new Date());
          return combined;
        });
      }, 8000); // Generate new event every 8 seconds for faster testing

      return () => clearInterval(mockInterval);
    }
  }, [USE_MOCK_DATA]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('sv-SE');
    const timeStr = date.toLocaleTimeString('sv-SE', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${dateStr} ${timeStr}`;
  };

  // Force refresh function for testing
  const forceRefresh = () => {
    setRefreshCounter(prev => prev + 1);
    setLoading(true);
    setError(null);

    if (USE_MOCK_DATA) {
      setTimeout(() => {
        const refreshedData = mockHistoryData.map(event => ({
          ...event,
          timestamp: new Date().toISOString() // Update timestamps to current time
        }));

        const sortedRefreshedData = refreshedData.sort((a, b) => {
          const dateA = new Date(a.timestamp);
          const dateB = new Date(b.timestamp);
          return dateB.getTime() - dateA.getTime();
        });



        setHistory(sortedRefreshedData);
        setLastUpdate(new Date());
        setCurrentPage(1); // Reset to first page to show refreshed data
        setLoading(false);
      }, 500);
    } else {
      // Trigger a new fetch by updating the refresh counter
      getApiHost(apiHost => {
        fetchApiRequest(`${apiHost}/v1/devices/history?limit=100&refresh=${refreshCounter}`, (fetchPromise) => {
          fetchPromise
            .then(res => res.ok ? res.json() : Promise.reject('Network response was not ok'))
            .then(data => {
              // Normalize timestamps to local time by subtracting 2 hours
              const normalizedData = data.map(event => ({
                ...event,
                timestamp: new Date(new Date(event.timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
              }));

              const sortedData = normalizedData.sort((a, b) => {
                const dateA = new Date(a.timestamp);
                const dateB = new Date(b.timestamp);
                return dateB.getTime() - dateA.getTime();
              });



              setHistory(sortedData);
              setLastUpdate(new Date());
              setCurrentPage(1); // Reset to first page to show refreshed data
              setLoading(false);
            })
            .catch(err => {
              setError(err.toString());
              setLoading(false);
            });
        });
      });
    }
  };

  useEffect(() => {
    const fetchData = (allHistory) => {
      // Normalize timestamps to local time by subtracting 2 hours (UTC+4 to local)
      const normalizedHistory = allHistory.map(event => ({
        ...event,
        timestamp: new Date(new Date(event.timestamp).getTime() - (2 * 60 * 60 * 1000)).toISOString()
      }));

      // Sort by timestamp in descending order (newest first)
      const sortedHistory = normalizedHistory.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateB.getTime() - dateA.getTime();
      });



      setHistory(sortedHistory);
      setCurrentPage(1); // Ensure we start on the first page
      setLoading(false);
    };

    setLoading(true);
    if (USE_MOCK_DATA) {
      setTimeout(() => fetchData(mockHistoryData), 300);
    } else {
      getApiHost(apiHost => {
        fetchApiRequest(`${apiHost}/v1/devices/history?limit=100`, (fetchPromise) => {
          fetchPromise
            .then(res => res.ok ? res.json() : Promise.reject('Network response was not ok'))
            .then(data => fetchData(data))
            .catch(err => {
              setError(err.toString());
              setLoading(false);
            });
        });
      });
    }
  }, []);

  useEffect(() => {
    if (!liveEvents) {
      return;
    }

    let incoming = [];
    if (liveEvents.devices?.length) {
      incoming = liveEvents.devices.map((e) => ({
        ...e,
        device_id: e.name || e.device_id,
        timestamp: e.timestamp,
        events: e.events || [],
        scores: e.scores || [],
      }));
    } else if (liveEvents.device_id) {
      incoming = [streamEventToDevice(liveEvents)];
    }

    if (incoming.length === 0) {
      return;
    }

    setHistory(prevHistory => {
      const incomingEvents = incoming.filter(newEvent => (
        !prevHistory.some(existingEvent => (
          existingEvent.timestamp === newEvent.timestamp
          && existingEvent.device_id === newEvent.device_id
        ))
      ));

      if (incomingEvents.length === 0) {
        return prevHistory;
      }

      const combined = [...incomingEvents, ...prevHistory];
      combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setCurrentPage(1);
      return combined;
    });
  }, [liveEvents]);

  // Update last update time whenever we receive live events
  useEffect(() => {
    if (liveEvents && (liveEvents.devices?.length || liveEvents.device_id)) {
      setLastUpdate(new Date());
    }
  }, [liveEvents]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error: {error}</Alert>;
  }

  const totalPages = Math.ceil(history.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentHistory = history.slice(startIndex, endIndex);

  const renderEventRows = (record) => {
    if (!record.events || !record.scores) {
      return (
        <Typography variant="body2" color="text.secondary">
          No events data
        </Typography>
      );
    }

    const rows = [];
    const eventsPerRow = 3;

    for (let i = 0; i < record.events.length; i += eventsPerRow) {
      const rowEvents = record.events.slice(i, i + eventsPerRow);
      const rowScores = record.scores.slice(i, i + eventsPerRow);

      rows.push(
        <Box key={i} sx={{ display: 'flex', gap: 0.2, mb: i + eventsPerRow < record.events.length ? 0.5 : 0 }}>
          {rowEvents.map((event, eventIndex) => (
            <Box
              key={eventIndex}
              sx={{
                minWidth: '120px',
                flex: '1 1 auto',
                maxWidth: '200px'
              }}
            >
              <ScoreGradientBar
                score={rowScores[eventIndex]}
                event={event}
                height={30}
                width="100%"
                showValue={true}
                displayMode={displayMode}
              />
            </Box>
          ))}
        </Box>
      );
    }
    return rows;
  };

  const tableContent = (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, fontWeight: 'bold' }}>
              Timestamp
            </TableCell>
            <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, fontWeight: 'bold' }}>
              Device ID
            </TableCell>
            <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, fontWeight: 'bold' }}>
              Events & Scores
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentHistory.map((record, index) => (
            <TableRow key={index} hover sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
              <TableCell sx={{ color: theme.palette.text.primary }}>
                {formatTimestamp(record.timestamp)}
              </TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>
                {record.device_id}
              </TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>
                {renderEventRows(record)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const paginationControls = totalPages > 1 && (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 1 }}>
      <IconButton onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} size="small">
        <ChevronLeftIcon />
      </IconButton>
      <Typography variant="body2" sx={{ mx: 2 }}>
        Page {currentPage} of {totalPages}
      </Typography>
      <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} size="small">
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 'calc(3 * 320px + 2 * 8px)', width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ color: theme.palette.text.primary }}>
            Prediction History ({history.length} events)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={USE_MOCK_DATA ? <ConnectedIcon /> : <ConnectedIcon />}
            label={USE_MOCK_DATA ? 'Mock Mode - Live Updates' : 'Live Updates Active'}
            color="success"
            variant="outlined"
            size="small"
            sx={{ paddingLeft: '0.4rem' }}
            onClick={(e) => e.preventDefault()}
          />
          <IconButton
            onClick={forceRefresh}
            size="small"
            sx={{ color: theme.palette.primary.main }}
            title="Force refresh history"
          >
            <RefreshIcon />
          </IconButton>
          {USE_MOCK_DATA && (
            <IconButton
              onClick={() => {
                const newEvent = generateNewMockEvents();

                setHistory(prevHistory => {
                  const combined = [newEvent, ...prevHistory];
                  combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                  setCurrentPage(1);
                  setLastUpdate(new Date());
                  return combined;
                });
              }}
              size="small"
              sx={{ color: 'warning.main' }}
              title="Generate test event"
            >
              <NotificationsIcon />
            </IconButton>
          )}
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            {history.length > 0 && (
              <Typography
                variant="caption"
                sx={{ color: 'success.main', fontWeight: 'bold' }}
              >
                ● Auto-cleanup every 2min
              </Typography>
            )}
            {lastUpdate && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                Last update: {formatTimestamp(lastUpdate.toISOString())}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {history.length === 0 ? (
        <Alert severity="info">No history found.</Alert>
      ) : (
        <>
          {tableContent}
          {paginationControls}
        </>
      )}
    </Box>
  );
}

export default History;